import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Loader2, Sparkles, Copy, Trash2, Save, ExternalLink, X, Edit,
  RectangleHorizontal, Square, Mail, Share2, ArrowLeft, Wand2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const LANDING_PAGES = [
  { value: '/', label: 'Homepage' },
  { value: '/services', label: 'Services Showcase' },
  { value: '/plan', label: 'Plan Your Event' },
  { value: '/events', label: 'Events Listing' },
  { value: '/denver', label: 'Denver Landing Page' },
  { value: '/las-vegas', label: 'Las Vegas Landing Page' },
  { value: '/los-angeles', label: 'Los Angeles Landing Page' },
  { value: '/charlotte', label: 'Charlotte Landing Page' },
];

const AD_SIZES = {
  banner_728: { w: 728, h: 90, label: 'Leaderboard Banner', icon: RectangleHorizontal },
  banner_970: { w: 970, h: 250, label: 'Billboard Banner', icon: RectangleHorizontal },
  box_300: { w: 300, h: 250, label: 'Medium Rectangle', icon: Square },
  box_336: { w: 336, h: 280, label: 'Large Rectangle', icon: Square },
  email_600: { w: 600, h: 200, label: 'Email Header', icon: Mail },
  social_1200: { w: 1200, h: 628, label: 'Social Share', icon: Share2 },
};

export default function AdCreator() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('create');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '', landing_page: '/services', target_city: '', event_type: '', tone: 'professional',
  });
  const [copy, setCopy] = useState(null);
  const [selectedSizes, setSelectedSizes] = useState(['banner_728', 'box_300', 'email_600']);
  const [editingCampaignId, setEditingCampaignId] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/ad-creator/campaigns`, { headers });
      setCampaigns(res.data.campaigns || []);
    } catch {}
  };

  const generateCopy = async () => {
    if (!form.name) { toast.error('Give your campaign a name first'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/ad-creator/generate-copy`, {
        campaign_name: form.name,
        landing_page: form.landing_page,
        target_city: form.target_city,
        event_type: form.event_type,
        tone: form.tone,
      }, { headers });
      setCopy(res.data.copy);
      toast.success('Ad copy generated!');
    } catch { toast.error('Generation failed'); }
    finally { setGenerating(false); }
  };

  const saveCampaign = async () => {
    if (!form.name || !copy) { toast.error('Generate copy first'); return; }
    setSaving(true);
    try {
      const ads = selectedSizes.map(sizeKey => ({
        size_key: sizeKey,
        ...AD_SIZES[sizeKey],
        html: generateAdHTML(sizeKey),
      }));
      const data = { name: form.name, landing_page: form.landing_page, landing_page_url: `${BASE}${form.landing_page}`, target_city: form.target_city, event_type: form.event_type, tone: form.tone, copy, ads };

      if (editingCampaignId) {
        await axios.put(`${API}/ad-creator/campaigns/${editingCampaignId}`, data, { headers });
        toast.success('Campaign updated');
      } else {
        await axios.post(`${API}/ad-creator/campaigns`, data, { headers });
        toast.success('Campaign saved');
      }
      fetchCampaigns();
      setActiveTab('campaigns');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const deleteCampaign = async (id) => {
    if (!confirm('Delete this campaign?')) return;
    await axios.delete(`${API}/ad-creator/campaigns/${id}`, { headers });
    toast.success('Deleted'); fetchCampaigns();
  };

  const loadCampaign = (c) => {
    setForm({ name: c.name, landing_page: c.landing_page || '/services', target_city: c.target_city || '', event_type: c.event_type || '', tone: c.tone || 'professional' });
    setCopy(c.copy);
    setSelectedSizes((c.ads || []).map(a => a.size_key).filter(Boolean));
    setEditingCampaignId(c.campaign_id);
    setActiveTab('create');
  };

  const generateAdHTML = (sizeKey) => {
    if (!copy) return '';
    const size = AD_SIZES[sizeKey];
    const url = `${BASE}${form.landing_page}`;
    const isSmall = size.h <= 100;
    const isEmail = sizeKey.startsWith('email');

    if (isEmail) {
      return `<div style="font-family:-apple-system,sans-serif;max-width:600px;margin:0 auto;">
  <div style="background:linear-gradient(135deg,#6366f1,#a855f7);padding:32px 24px;border-radius:12px 12px 0 0;text-align:center;">
    <h1 style="color:#fff;margin:0;font-size:24px;">${copy.headline}</h1>
    <p style="color:rgba(255,255,255,.85);margin:8px 0 0;font-size:15px;">${copy.subheadline}</p>
  </div>
  <div style="padding:24px;background:#fff;border:1px solid #e2e8f0;border-top:none;">
    <p style="color:#475569;line-height:1.6;margin:0 0 16px;">${copy.body_copy}</p>
    <a href="${url}" style="display:inline-block;background:#6366f1;color:#fff;padding:12px 28px;text-decoration:none;border-radius:8px;font-weight:bold;">${copy.cta_text}</a>
  </div>
  <div style="padding:12px 24px;background:#f8fafc;border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;text-align:center;">
    <p style="margin:0;font-size:11px;color:#94a3b8;">Eventism - Your Event, Our Expertise | <a href="${BASE}" style="color:#6366f1;">eventism.com</a></p>
  </div>
</div>`;
    }

    return `<a href="${url}" target="_blank" style="display:inline-block;width:${size.w}px;height:${size.h}px;overflow:hidden;border-radius:8px;text-decoration:none;background:linear-gradient(135deg,#1e1b4b,#312e81);position:relative;font-family:-apple-system,sans-serif;">
  <div style="position:absolute;inset:0;padding:${isSmall ? '12px 20px' : '20px 28px'};display:flex;${isSmall ? 'align-items:center;gap:20px;' : 'flex-direction:column;justify-content:center;'}">
    <div style="flex:1;">
      <div style="font-size:${isSmall ? '16px' : '22px'};font-weight:800;color:#fff;line-height:1.2;">${copy.headline}</div>
      ${!isSmall ? `<div style="font-size:14px;color:rgba(255,255,255,.75);margin-top:6px;">${copy.subheadline}</div>` : ''}
    </div>
    <div style="display:inline-block;background:#6366f1;color:#fff;padding:${isSmall ? '8px 16px' : '10px 22px'};border-radius:6px;font-weight:700;font-size:${isSmall ? '12px' : '14px'};white-space:nowrap;">${copy.cta_text}</div>
  </div>
  <div style="position:absolute;bottom:${isSmall ? '4px' : '8px'};right:12px;font-size:10px;color:rgba(255,255,255,.4);font-weight:600;">EVENTISM</div>
</a>`;
  };

  const copyToClipboard = (text, label) => {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); toast.success(`${label} copied!`); } catch { toast.error('Copy failed'); }
    document.body.removeChild(ta);
  };

  return (
    <div className="min-h-screen bg-background pt-16 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-6 pt-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Digital Ad Creator</h1>
            <p className="text-sm text-muted-foreground">Generate AI-powered ad campaigns with HTML embeds</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="create" className="flex-1" data-testid="tab-create"><Wand2 className="h-4 w-4 mr-2" /> Create Campaign</TabsTrigger>
            <TabsTrigger value="campaigns" className="flex-1" data-testid="tab-campaigns"><Sparkles className="h-4 w-4 mr-2" /> My Campaigns ({campaigns.length})</TabsTrigger>
          </TabsList>

          {/* CREATE TAB */}
          <TabsContent value="create">
            <div className="grid lg:grid-cols-5 gap-6">
              {/* Config Panel */}
              <div className="lg:col-span-2 space-y-4">
                <Card className="border-border">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="font-semibold text-sm">Campaign Setup</h3>
                    <div className="space-y-1"><Label className="text-xs">Campaign Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Summer Vegas Push" data-testid="camp-name" /></div>
                    <div className="space-y-1"><Label className="text-xs">Landing Page</Label>
                      <select value={form.landing_page} onChange={e => setForm(p => ({ ...p, landing_page: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="camp-landing">
                        {LANDING_PAGES.map(lp => <option key={lp.value} value={lp.value}>{lp.label} ({lp.value})</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Target City</Label>
                        <select value={form.target_city} onChange={e => setForm(p => ({ ...p, target_city: e.target.value }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm">
                          <option value="">All Markets</option><option value="Denver">Denver</option><option value="Las Vegas">Las Vegas</option><option value="Los Angeles">Los Angeles</option><option value="Charlotte">Charlotte</option>
                        </select>
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Tone</Label>
                        <select value={form.tone} onChange={e => setForm(p => ({ ...p, tone: e.target.value }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm">
                          <option value="professional">Professional</option><option value="fun">Fun & Playful</option><option value="luxury">Luxury</option><option value="urgent">Urgent / FOMO</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Event Focus (optional)</Label><Input value={form.event_type} onChange={e => setForm(p => ({ ...p, event_type: e.target.value }))} placeholder="Corporate, Wedding, Birthday..." /></div>
                    <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={generateCopy} disabled={generating} data-testid="generate-btn">
                      {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</> : <><Wand2 className="h-4 w-4 mr-2" /> Generate Ad Copy</>}
                    </Button>
                  </CardContent>
                </Card>

                {/* Ad Sizes */}
                <Card className="border-border">
                  <CardContent className="p-4 space-y-2">
                    <h3 className="font-semibold text-sm">Ad Formats</h3>
                    {Object.entries(AD_SIZES).map(([key, size]) => {
                      const Icon = size.icon;
                      return (
                        <label key={key} className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${selectedSizes.includes(key) ? 'border-indigo-500 bg-indigo-500/5' : 'border-border hover:border-indigo-500/30'}`}>
                          <input type="checkbox" checked={selectedSizes.includes(key)} onChange={() => setSelectedSizes(p => p.includes(key) ? p.filter(s => s !== key) : [...p, key])} className="rounded" />
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs flex-1">{size.label}</span>
                          <span className="text-[10px] text-muted-foreground">{size.w}x{size.h}</span>
                        </label>
                      );
                    })}
                  </CardContent>
                </Card>

                {copy && (
                  <Button className="w-full" onClick={saveCampaign} disabled={saving} data-testid="save-campaign">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {editingCampaignId ? 'Update Campaign' : 'Save Campaign'}
                  </Button>
                )}
              </div>

              {/* Preview & Copy Panel */}
              <div className="lg:col-span-3 space-y-4">
                {!copy ? (
                  <Card className="border-border text-center py-16"><CardContent><Wand2 className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" /><p className="text-sm text-muted-foreground">Configure your campaign and click "Generate Ad Copy"</p></CardContent></Card>
                ) : (
                  <>
                    {/* Editable Copy */}
                    <Card className="border-border">
                      <CardContent className="p-4 space-y-3">
                        <h3 className="font-semibold text-sm">Ad Copy <Badge variant="outline" className="text-[9px] ml-1">Editable</Badge></h3>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div className="space-y-1"><Label className="text-xs">Headline</Label><Input value={copy.headline} onChange={e => setCopy(p => ({ ...p, headline: e.target.value }))} className="font-semibold" data-testid="copy-headline" /></div>
                          <div className="space-y-1"><Label className="text-xs">CTA Button</Label><Input value={copy.cta_text} onChange={e => setCopy(p => ({ ...p, cta_text: e.target.value }))} data-testid="copy-cta" /></div>
                        </div>
                        <div className="space-y-1"><Label className="text-xs">Subheadline</Label><Input value={copy.subheadline} onChange={e => setCopy(p => ({ ...p, subheadline: e.target.value }))} /></div>
                        <div className="space-y-1"><Label className="text-xs">Body Copy</Label><Textarea value={copy.body_copy} onChange={e => setCopy(p => ({ ...p, body_copy: e.target.value }))} rows={2} className="text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs">Social Caption</Label><Textarea value={copy.social_caption} onChange={e => setCopy(p => ({ ...p, social_caption: e.target.value }))} rows={2} className="text-sm" /></div>
                        {copy.alt_headlines && (
                          <div><Label className="text-xs text-muted-foreground">Alt Headlines (click to use)</Label>
                            <div className="flex gap-1 flex-wrap mt-1">{copy.alt_headlines.map((h, i) => (
                              <Badge key={i} variant="outline" className="text-xs cursor-pointer hover:bg-indigo-500/10" onClick={() => setCopy(p => ({ ...p, headline: h }))}>{h}</Badge>
                            ))}</div>
                          </div>
                        )}
                        {copy.alt_ctas && (
                          <div><Label className="text-xs text-muted-foreground">Alt CTAs (click to use)</Label>
                            <div className="flex gap-1 flex-wrap mt-1">{copy.alt_ctas.map((c, i) => (
                              <Badge key={i} variant="outline" className="text-xs cursor-pointer hover:bg-indigo-500/10" onClick={() => setCopy(p => ({ ...p, cta_text: c }))}>{c}</Badge>
                            ))}</div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Ad Previews */}
                    {selectedSizes.map(sizeKey => {
                      const size = AD_SIZES[sizeKey];
                      const html = generateAdHTML(sizeKey);
                      return (
                        <Card key={sizeKey} className="border-border overflow-hidden">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-semibold">{size.label}</h4>
                                <Badge variant="outline" className="text-[9px]">{size.w}x{size.h}</Badge>
                              </div>
                              <Button size="sm" variant="outline" className="text-xs" onClick={() => copyToClipboard(html, `${size.label} HTML`)} data-testid={`copy-${sizeKey}`}>
                                <Copy className="h-3 w-3 mr-1" /> Copy HTML
                              </Button>
                            </div>
                            <div className="bg-[#f0f0f0] p-4 rounded-lg flex justify-center overflow-x-auto">
                              <div dangerouslySetInnerHTML={{ __html: html }} style={{ maxWidth: '100%' }} />
                            </div>
                            <details className="text-xs">
                              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View HTML Code</summary>
                              <pre className="mt-2 p-3 bg-[#0f172a] text-[#94a3b8] rounded-lg text-[10px] overflow-x-auto whitespace-pre-wrap font-mono max-h-32">{html}</pre>
                            </details>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* CAMPAIGNS TAB */}
          <TabsContent value="campaigns">
            <div className="space-y-3" data-testid="campaign-list">
              {campaigns.length === 0 ? (
                <Card className="text-center py-12"><CardContent><Sparkles className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No campaigns yet</p></CardContent></Card>
              ) : campaigns.map(c => (
                <Card key={c.campaign_id} className="border-border hover:border-indigo-500/30 transition-colors">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-5 w-5 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{c.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-muted-foreground">{c.target_city || 'All markets'}</span>
                        <span className="text-[10px] text-muted-foreground">|</span>
                        <span className="text-[10px] text-muted-foreground">{c.landing_page}</span>
                        <span className="text-[10px] text-muted-foreground">|</span>
                        <span className="text-[10px] text-muted-foreground">{(c.ads || []).length} ad formats</span>
                      </div>
                      {c.copy?.headline && <p className="text-xs text-muted-foreground mt-1 truncate">"{c.copy.headline}"</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => loadCampaign(c)}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteCampaign(c.campaign_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
